import _ from "lodash";
import React, { FC, useEffect, useState } from "react";
import { Prompt, useHistory } from "react-router-dom";

import ConfirmNavModal from "../../../Lumber/ConfirmNavModal";
import { useProtocolAnalysis, useProtocolAnalysisDispatch } from "./ProtocolAnalysisContext";
import { useSaveMetaTags } from "./queries";
import type { FlattenedTrialMetadata } from "./types";

type UnsavedMetaTagsGuardProps = {
	trialMetadata: FlattenedTrialMetadata[] | undefined;
};

/**
 * Blocks navigating away while there are unsaved meta tag edits, and offers to save or discard
 * them. Renders nothing itself. Must be inside ProtocolAnalysisProvider.
 */
const UnsavedMetaTagsGuard: FC<UnsavedMetaTagsGuardProps> = ({ trialMetadata }) => {
	const history = useHistory();
	const { hasUnsavedMetaTags } = useProtocolAnalysis();
	const { clearMetaTagEdits, changeDisplaySettings } = useProtocolAnalysisDispatch();
	const { save } = useSaveMetaTags(_.map(trialMetadata, (tm) => tm.trialInfo));

	const [open, setOpen] = useState(false);
	const [target, setTarget] = useState("");

	// Save and discard clear the edits, which lifts the Prompt; only then can the blocked navigation go through
	useEffect(() => {
		if (!hasUnsavedMetaTags && target !== "") {
			history.push(target);
		}
	}, [history, hasUnsavedMetaTags, target]);

	return (
		<>
			<Prompt
				when={hasUnsavedMetaTags}
				message={(location) => {
					setTarget(location.pathname + location.search);
					setOpen(true);
					return false;
				}}
			/>
			<ConfirmNavModal
				open={open}
				onCancel={() => {
					setTarget("");
					setOpen(false);
				}}
				onSave={() => {
					void save().then((saved) => {
						if (!saved) return;
						changeDisplaySettings({ isEditingMetaTags: false });
						setOpen(false);
					});
				}}
				onDiscard={() => {
					clearMetaTagEdits();
					changeDisplaySettings({ isEditingMetaTags: false });
					setOpen(false);
				}}
			/>
		</>
	);
};

export default UnsavedMetaTagsGuard;
